import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';

import {
  countDays,
  EditVacationsProposalAction,
  SEASONS
} from '@app/activo2/manage-vacations/manage-vacations.constants';
import { ICreateProposal, IProposal, UpdateProposal } from '@app/activo2/manage-vacations/manage-vacations.interface';
import {
  MotSeasonProposal,
  VacationsProposal,
  VacationsSeason
} from '@app/activo2/manage-vacations/manage-vacations.model';
import { BaseManageVacationsPage } from '@app/activo2/manage-vacations/manage-vacations.page';
import { ConfirmationModalComponent } from '@app/components/modals/confirmation-modal/confirmation-modal.component';
import { NavigationEvent, NavigationEvents } from '@app/services';
import { PAGES } from '@app/shared/enums/pages/pages.enum';
import { getUrlFromMap } from '@app/shared/enums/pages/pages.urls';
import { Buttons } from '@app/shared/models/buttons/buttons';
import { isNullOrUndefined } from '@app/shared/utils/utils';

import { CriteriaModalComponent } from './components/criteria-modal/criteria-modal.component';

@Component({
  selector: 'app-mot-manage-vacations',
  templateUrl: './manage-vacations.page.html',
  styleUrls: ['./manage-vacations.page.scss']
})
export class MotManageVacationsPage implements OnInit {
  @Input() base!: BaseManageVacationsPage;
  @Output() manageProposal: EventEmitter<UpdateProposal> = new EventEmitter<UpdateProposal>();

  get stepperTitle() {
    let title = '';

    switch (this.base.currentStep) {
      case 1:
        title = 'MANAGE_VACATIONS.VACATIONS_TYPE';
        break;
      case 2:
        title = 'MANAGE_VACATIONS.CHECK_VACATIONS_CRITERIA';
        break;
      case 3:
        title = 'MANAGE_VACATIONS.SELECT_PROPOSAL';
        break;
    }

    return title;
  }

  get showStepper() {
    return (
      (!this.base.proposals || !this.base.proposals.userHasData() || this.base.currentStep !== 1) &&
      this.base.hasEditPermission
    );
  }

  get seasonProposal(): MotSeasonProposal {
    return this.base.seasonProposal as MotSeasonProposal;
  }

  ngOnInit() {
    const action: EditVacationsProposalAction = history.state?.action;
    const order: number = history.state?.order;

    if (isNullOrUndefined(action)) {
      return;
    }

    this.base.seasonProposal = this.base.manageVacationsService.currentEditingProposal.value;

    switch (action) {
      case EditVacationsProposalAction.ADD:
        this.base.currentStep = 3;
        this.base.modifyingOrder = order;
        this.base.backDisabled = false;
        this.base.nextDisabled = true;
        if (!this.base.selectedSeason) {
          this.base.setSelectedSeasonByProposal(this.base.seasonProposal);
        }
        this.updateButtonsTexts();
        this.base.loadThirdStep(true);
        break;
      case EditVacationsProposalAction.EDIT:
        this.base.currentStep = 3;
        this.base.initialSlots = this.base.seasonProposal.getProposalsByOrder(order);
        this.base.modifyingOrder = order;
        this.base.backDisabled = false;
        this.base.nextDisabled = Boolean(!this.base.initialSlots.length);
        if (!this.base.selectedSeason) {
          this.base.setSelectedSeasonByProposal(this.base.seasonProposal);
        }
        this.updateButtonsTexts();
        this.base.loadThirdStep(true);
        break;
      case EditVacationsProposalAction.DELETE:
        this.manageOrderedProposals(true);
        break;
      case EditVacationsProposalAction.SAVE_CHANGES:
        this.manageOrderedProposals(false);
        break;
    }

    history.replaceState({}, '');
  }

  setSeason(selectedSeason: VacationsSeason) {
    this.base.selectedSeason = selectedSeason;
    this.base.notSelectedSeason = this.base.seasons.find((season) => selectedSeason.id !== season.id);

    this.updateStep(true);
  }

  setSlots(slots: IProposal[]) {
    this.base.nextDisabled = countDays(slots) < this.base.seasonCalendar.minHolidays;
    this.base.slots = slots;
  }

  editProposal(seasonProposal: MotSeasonProposal, editedProposal: boolean) {
    this.base.setSelectedSeasonByProposal(seasonProposal);
    this.base.manageVacationsService.currentEditingProposal.next(seasonProposal);
    this.base.manageVacationsService.editedProposal = editedProposal;

    const navEvent = new NavigationEvent(NavigationEvents.Push, {
      path: `${getUrlFromMap(PAGES.VACATIONS_REQUEST)}/edit`
    });

    this.base.navigationService.navigate(navEvent);
  }

  async updateStep(goingForward: boolean) {
    const isLastStep = this.base.currentStep === 3;

    if (isLastStep) {
      const isShowingResume = this.base.showResumeView;

      if (isShowingResume) {
        return await this.#manageWhenIsShowingResume(goingForward);
      } else if (goingForward) {
        if (this.seasonProposal.checkProposalAlreadyAdded(this.base.slots)) {
          this.base.alertService.showWarning(
            this.base.messages['INFO_TOASTS.INFO_TITLE'],
            this.base.messages['MANAGE_VACATIONS.SLOT_ALREADY_ADDED']
          );
          return;
        }

        return this.#updateProposals();
      } else if (this.base.modifyingOrder) {
        // We pressed back on calendar view after click on edit
        this.base.initialSlots = this.seasonProposal.getProposalsByOrder(this.base.modifyingOrder);
        this.editProposal(this.seasonProposal, false);
        return;
      }
    }

    this.#updateCurrentStep(goingForward);

    this.base.isCalendarView = isLastStep && !this.base.showResumeView;
    this.base.showInfoIcon = isLastStep;

    this.#manageButtonsNavigation();
  }

  async #manageWhenIsShowingResume(goingForward: boolean) {
    if (goingForward) {
      this.base.showResumeView = false;
      this.base.nextDisabled = true;
      this.base.slots = [];
      this.base.initialSlots = [];
      this.base.resetCalendar();
      this.updateButtonsTexts();
      return;
    }

    if (this.seasonProposal.hasAllProposalsFullfilled()) {
      this.manageOrderedProposals(false);
    } else {
      const confirmed = await this.showConfirmationModal();

      if (confirmed) {
        this.manageOrderedProposals(false);
      } else {
        this.base.showResumeView = true;
        this.updateButtonsTexts();
      }
    }
  }

  #updateProposals() {
    // We are adding or editing a proposal
    if (this.base.modifyingOrder) {
      this.modifyProposalsByOrder();
    } else {
      if (this.seasonProposal.fullfillingLastProposal()) {
        this.base.nextDisabled = true;
      }

      this.base.showResumeView = true;
      this.updateButtonsTexts();
    }
  }

  #updateCurrentStep(goingForward: boolean) {
    if (goingForward) {
      this.base.currentStep++;
    } else {
      this.base.currentStep--;
    }
  }

  #manageButtonsNavigation() {
    switch (this.base.currentStep) {
      case 1:
        this.base.initialSlots = [];
        this.base.slots = [];
        break;
      case 2:
        this.base.nextDisabled = false;
        this.base.backDisabled = false;
        this.base.initialSlots = this.base.slots;
        break;
      case 3:
        this.base.nextDisabled = this.base.seasonCalendar
          ? countDays(this.base.initialSlots) < this.base.seasonCalendar.minHolidays
          : true;
        this.updateButtonsTexts();
        this.base.loadThirdStep(false);
        break;
    }
  }

  addProposals(proposal: MotSeasonProposal) {
    this.base.setSelectedSeasonByProposal(proposal);
    this.base.getCriteria();
    this.updateStep(true);
  }

  showConfirmationModal() {
    const title = 'MANAGE_VACATIONS.SEND_PROPOSAL_CONFIRMATION';
    const contents = [
      'MANAGE_VACATIONS.SEND_PROPOSAL_CONFIRMATION_MSG_1',
      'MANAGE_VACATIONS.SEND_PROPOSAL_CONFIRMATION_MSG_2'
    ];
    const buttons: Buttons[] = [
      {
        text: 'NO',
        type: 'secondary',
        enabled: true,
        onClick: () => {
          this.base.modalManager.dismissMatModal();
        }
      },
      {
        text: 'YES',
        type: 'primary',
        enabled: true,
        onClick: () => {
          this.base.modalManager.dismissMatModal(true);
        }
      }
    ];

    const modalOpts: MatDialogConfig = {
      data: { title, contents, buttons },
      disableClose: true,
      panelClass: ['media-content-modal', 'confirmation-two-lines-modal']
    };

    return this.base.modalManager.openMatModal(ConfirmationModalComponent, modalOpts);
  }

  async onInfoIconClicked() {
    if (!this.base.criteria) {
      await this.base.getCriteria();
    }

    const modalOpts = {
      data: { criteria: this.base.criteria, selectedSeason: this.base.selectedSeason },
      disableClose: true,
      panelClass: 'media-content-modal'
    };

    this.base.modalManager.openMatModal(CriteriaModalComponent, modalOpts);
  }

  manageOrderedProposals(deleting: boolean) {
    this.base.seasonProposal = this.base.seasonProposal as MotSeasonProposal;
    this.base.proposals = this.base.proposals as VacationsProposal;

    const proposals: ICreateProposal = {
      winter_proposals: [],
      summer_proposals: [],
      employee_observations: ''
    };

    if (this.base.seasonProposal.id === SEASONS.summer) {
      proposals.summer_proposals = this.base.seasonProposal.proposals;
      proposals.winter_proposals = this.base.proposals?.winter.proposals || [];
    } else {
      proposals.winter_proposals = this.base.seasonProposal.proposals;
      proposals.summer_proposals = this.base.proposals?.summer.proposals || [];
    }

    if (this.base.proposals && this.base.proposals.observations) {
      proposals.employee_observations = this.base.proposals.observations.content;
    }

    this.base.showResumeView = false;
    this.updateButtonsTexts();
    this.manageProposal.emit({ proposals, deleting });
  }

  modifyProposalsByOrder() {
    this.base.seasonProposal = this.base.seasonProposal as MotSeasonProposal;

    this.base.seasonProposal.removeProposalsByOrder(this.base.modifyingOrder);
    this.base.slots.forEach((slot) => (slot.order = this.base.modifyingOrder));
    this.base.seasonProposal.addSlotsToSeasonProposal(this.base.slots);
    this.base.seasonProposal.groupProposalsByOrder();
    this.editProposal(this.seasonProposal, true);
  }

  updateButtonsTexts() {
    if (this.base.showResumeView) {
      this.base.backButtonText = 'MANAGE_VACATIONS.SEND_PROPOSAL';
      this.base.nextButtonText = 'MANAGE_VACATIONS.ADD_ANOTHER_PROPOSAL';
    } else {
      this.base.backButtonText = 'STEPPER.BACK';
      this.base.nextButtonText = 'STEPPER.NEXT';
    }
    this.base.isCalendarView = this.base.currentStep === 3 && !this.base.showResumeView;
    this.base.showInfoIcon = this.base.currentStep === 3;
  }
}
